/**
 * Authentication Context
 * Manages user authentication state throughout the app
 *
 * Per AERO-EDC spec:
 * - mustChangePassword flag for first login / password reset
 * - Password change returns new token
 * - Inactivity logout after 30 minutes
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi, User, ApiError } from '../services/api';
import { queryClient } from '../queryClient';

// Inactivity timeout in milliseconds (30 minutes)
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
  studyId: string | null;
  studyCode: string | null;
  studyName: string | null;
  login: (username: string, password: string, studyId: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAuthenticatedRef = useRef(false);

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    if (!isAuthenticatedRef.current) return;

    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set new timer
    inactivityTimerRef.current = setTimeout(() => {
      console.log('Logging out due to inactivity');
      // Clear auth state
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      isAuthenticatedRef.current = false;
      // Alert user
      alert('You have been logged out due to inactivity.');
    }, INACTIVITY_TIMEOUT);
  }, []);

  // Set up activity listeners
  useEffect(() => {
    if (!user) {
      isAuthenticatedRef.current = false;
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      return;
    }

    isAuthenticatedRef.current = true;

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttle activity resets to avoid too many timer resets
    let lastReset = Date.now();
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastReset > 1000) { // Only reset every 1 second
        lastReset = now;
        resetInactivityTimer();
      }
    };

    // Add listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [user, resetInactivityTimer]);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await authApi.me();
          setUser(response.user);
          setToken(storedToken);
        } catch (err) {
          // Token invalid or expired
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async (username: string, password: string, studyId: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await authApi.login(username, password, studyId);
      localStorage.setItem('token', response.token);
      setToken(response.token);
      setUser(response.user);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Login failed. Please try again.');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      // Continue with logout even if API call fails
    } finally {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      queryClient.clear();
    }
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    setError(null);
    // Do NOT toggle the global isLoading here: ProtectedRoute swaps the page for a
    // spinner while isLoading is true, which unmounts ChangePassword and wipes its
    // form/success state, so the user never sees the "Password Changed" screen.

    try {
      const response = await authApi.changePassword(currentPassword, newPassword);
      // Backend returns new token with mustChangePassword = false
      if (response.token) {
        localStorage.setItem('token', response.token);
        setToken(response.token);
      }
      if (response.user) {
        setUser(response.user);
      } else if (user) {
        // Update local user state
        setUser({ ...user, mustChangePassword: false });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Password change failed. Please try again.');
      }
      throw err;
    }
  }, [user]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    mustChangePassword: user?.mustChangePassword || false,
    studyId: user?.studyId || null,
    studyCode: user?.studyCode || null,
    studyName: user?.studyName || null,
    login,
    logout,
    changePassword,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
